import React from "react";
import { Helmet } from "react-helmet";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageTitle } from "@/components/PageTitle";
import { CTASection } from "@/components/CTASection";
import { SectionTitle } from "@/components/SectionTitle";
import { FAQAccordion } from "@/components/FAQAccordion";
import { ServiceTestimonials } from "@/components/ServiceTestimonials";
import { Smile } from "lucide-react";

const GumCleaning: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Gum Cleaning in Nagpur | Cost of Gum Cleaning in Nagpur</title>
        <meta
          name="description"
          content="Professional gum cleaning services at Asrani Dental Clinic in Nagpur. Expert care for healthy gums and prevention of periodontal disease. Schedule your appointment today!"
        />
      </Helmet>

      <Header />

      <main className="pt-20">
        <PageTitle
          title="Gum Cleaning"
        />

        {/* Service Overview Section */}
        <section className="py-16 bg-dental-offwhite">
          <div className="container-custom">
            <div className="flex flex-col md:flex-row gap-12 items-center">
              <div className="flex-1">
                <h2 className="text-3xl font-bold mb-6 text-dental-text">
                  Professional Gum Cleaning at Asrani Dental, Sadar
                </h2>
                <p className="mb-4">
                  Gum cleaning, also known as scaling and root planing, is a thorough cleaning procedure designed to treat gum disease and maintain gum health. At Asrani Dental Clinic in Nagpur, our experienced dental team provides comprehensive gum cleaning services to keep your gums healthy and prevent periodontal disease.
                </p>
                <p className="mb-4">
                  This deep cleaning procedure goes beyond regular teeth cleaning to remove plaque and tartar (calculus) deposits that have built up on the teeth and under the gum line, which can cause gum disease, bad breath, and other dental issues.
                </p>
                <p className="mb-6">
                  Our gentle approach ensures your comfort while effectively treating your gums and protecting your overall oral health.
                </p>
                <div className="flex items-center p-4 bg-white rounded-lg shadow-sm border border-gray-100">
                  <Smile className="w-8 h-8 text-dental-highlight mr-4" />
                  <div>
                    <h3 className="font-semibold">Advanced Gum Cleaning</h3>
                    <p className="text-sm text-gray-600">Our clinic uses state-of-the-art ultrasonic scaling techniques for more efficient and comfortable gum cleaning.</p>
                  </div>
                </div>
              </div>
              <div className="flex-1">
                <img
                  src="/lovable-uploads/professional_teeth_cleaning.jpg"
                  alt="Professional Gum Cleaning"
                  className="rounded-lg shadow-lg w-full"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Treatment Features */}
        <section className="py-16">
          <div className="container-custom">
            <SectionTitle
              title="Our Gum Cleaning Services"
              subtitle="Comprehensive Care for Your Periodontal Health"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-dental-offwhite rounded-full flex items-center justify-center mb-4">
                  <Smile className="w-6 h-6 text-dental-highlight" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Scaling</h3>
                <p>
                  Removal of tartar (calculus) and plaque from the tooth surface, both above and below the gum line, using specialized dental instruments.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-dental-offwhite rounded-full flex items-center justify-center mb-4">
                  <Smile className="w-6 h-6 text-dental-highlight" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Root Planing</h3>
                <p>
                  Smoothing of the tooth root surfaces to remove infected tooth structure and biofilm, helping gums reattach to teeth and heal properly.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-dental-offwhite rounded-full flex items-center justify-center mb-4">
                  <Smile className="w-6 h-6 text-dental-highlight" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Laser Therapy</h3>
                <p>
                  Advanced laser treatments to remove bacteria and infected tissue, promoting faster healing and reduced discomfort.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-dental-offwhite rounded-full flex items-center justify-center mb-4">
                  <Smile className="w-6 h-6 text-dental-highlight" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Antibiotic Treatment</h3>
                <p>
                  Application of local antibiotics in deep pockets to eliminate infection and support gum healing after cleaning.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-dental-offwhite rounded-full flex items-center justify-center mb-4">
                  <Smile className="w-6 h-6 text-dental-highlight" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Periodontal Maintenance</h3>
                <p>
                  Ongoing care program to monitor gum health and prevent recurrence of gum disease after treatment.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-dental-offwhite rounded-full flex items-center justify-center mb-4">
                  <Smile className="w-6 h-6 text-dental-highlight" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Home Care Education</h3>
                <p>
                  Personalized guidance on maintaining gum health between dental visits with proper brushing, flossing, and oral hygiene techniques.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 bg-dental-offwhite">
          <div className="container-custom">
            <SectionTitle
              title="Why Choose Us for Gum Cleaning"
              subtitle="Expert Periodontal Care at Asrani Dental, Nagpur"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="w-8 h-8 rounded-full bg-dental-highlight flex items-center justify-center text-white mr-3">1</span>
                  Specialized Expertise
                </h3>
                <p>
                  Our dentists are specially trained in periodontal treatments and use the latest techniques for effective gum cleaning.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="w-8 h-8 rounded-full bg-dental-highlight flex items-center justify-center text-white mr-3">2</span>
                  Comfortable Experience
                </h3>
                <p>
                  We prioritize your comfort during gum cleaning with gentle techniques, local anesthesia when needed, and a relaxing environment.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="w-8 h-8 rounded-full bg-dental-highlight flex items-center justify-center text-white mr-3">3</span>
                  Comprehensive Approach
                </h3>
                <p>
                  We address both your immediate gum health needs and provide education for long-term maintenance and disease prevention.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="w-8 h-8 rounded-full bg-dental-highlight flex items-center justify-center text-white mr-3">4</span>
                  Advanced Technology
                </h3>
                <p>
                  Our clinic utilizes modern equipment like ultrasonic scalers and laser therapy for more efficient, effective, and comfortable gum treatments.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section - Fixed props */}
        <ServiceTestimonials
          testimonials={[
            {
              name: "Anjali Patel",
              comment: "I was worried about my gum cleaning appointment, but the team at Asrani Dental made it a comfortable experience. My gums feel healthier than they have in years!",
              rating: 5,
              image: "/placeholder.svg"
            },
            {
              name: "Vikram Joshi",
              comment: "After suffering from bleeding gums for months, the deep cleaning at Asrani Dental has made a world of difference. The dentist explained everything clearly and the results are excellent.",
              rating: 5,
              image: "/placeholder.svg"
            }
          ]}
          title="What Our Patients Say"
          subtitle="Real Experiences from Patients at Asrani Dental Clinic in Nagpur"
        />

        {/* FAQ Section - Fixed props */}
        <section className="py-16 bg-dental-offwhite">
          <div className="container-custom">
            <SectionTitle
              title="Frequently Asked Questions"
              subtitle="Common Questions About Gum Cleaning in Nagpur"
            />

            <div className="mt-12 max-w-3xl mx-auto">
              <FAQAccordion
                faqs={[
                  {
                    question: "Is gum cleaning painful?",
                    answer: "Most patients experience minimal discomfort during gum cleaning. For deeper cleanings, we offer local anesthesia to ensure your comfort. Modern techniques and equipment have made the procedure much more comfortable than in the past."
                  },
                  {
                    question: "How often should I get a gum cleaning?",
                    answer: "The frequency depends on your individual gum health. Patients with healthy gums typically need professional cleaning twice a year, while those with gum disease may need more frequent deep cleanings, usually every 3-4 months."
                  },
                  {
                    question: "How long does a gum cleaning appointment take?",
                    answer: "A standard gum cleaning typically takes 45-60 minutes. For deep cleaning (scaling and root planing), the procedure is usually done in sections and may require multiple appointments of 1-2 hours each."
                  },
                  {
                    question: "What's the difference between regular teeth cleaning and gum cleaning?",
                    answer: "Regular teeth cleaning (prophylaxis) removes plaque and tartar from tooth surfaces above the gum line. Gum cleaning (scaling and root planing) is more extensive, removing deposits below the gum line and smoothing rough spots on tooth roots where bacteria accumulate."
                  },
                  {
                    question: "How can I maintain my gum health after a cleaning?",
                    answer: "Maintain good oral hygiene by brushing twice daily with fluoride toothpaste, flossing daily, using an antimicrobial mouthwash, eating a balanced diet, avoiding tobacco products, and keeping regular dental checkups and cleanings."
                  }
                ]}
              />
            </div>
          </div>
        </section>

        {/* CTA Section - Fixed props */}
        <CTASection
          title="Schedule Your Gum Cleaning Today"
          description="Protect your oral health with professional gum cleaning at Asrani Dental Clinic in Nagpur."
          primaryButtonText="Book Appointment"
          primaryButtonLink="/dental-appointment-booking"
        />
      </main>

      <Footer />
    </>
  );
};

export default GumCleaning;
